import { useEffect, useState, useMemo } from 'react';
import { AlertTriangle, CheckCircle2, XCircle, Search, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../ui/Spinner';
import { PageHeader } from '../ui/PageHeader';

/**
 * Pestaña admin: Alumnos en Riesgo.
 *
 * Para cada alumno con `final_average < 4.0` en algún course_evaluation,
 * mostramos: promedio + asistencia detallada (asistidas / faltadas / %).
 *
 * Si bajaramos el threshold (ej. < 5.0) entrarían más alumnos. Por ahora
 * 4.0 es la nota mínima de aprobación en Chile, así que todo lo que esté
 * abajo es "reprobando".
 */
interface RiskRow {
  alumnoId:    string;
  fullName:    string;
  email:       string;
  cursoId:     string;
  cursoName:   string;
  promedio:    number;
  clasesAsistidas: number;
  clasesTotales:   number;
  porcAsistencia:  number;
}

export function AlumnosEnRiesgoPage() {
  const [rows, setRows]       = useState<RiskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { data: evals } = await supabase
        .from('course_evaluations')
        .select(`
          alumno_id, course_id, final_average,
          alumno:alumno_id (id, full_name, email),
          curso:course_id (id, name)
        `);

      const enRiesgo = (evals ?? []).filter((e: any) => {
        const avg = typeof e.final_average === 'string' ? parseFloat(e.final_average) : e.final_average;
        return avg && avg > 0 && avg < 4.0;
      });

      if (enRiesgo.length === 0) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Bulk fetch para no hacer N+1
      const cursoIds  = [...new Set(enRiesgo.map((e: any) => e.course_id))];
      const alumnoIds = [...new Set(enRiesgo.map((e: any) => e.alumno_id))];

      const [{ data: modules }, { data: attendance }] = await Promise.all([
        supabase.from('modules').select('id, course_id, end_date').in('course_id', cursoIds),
        supabase.from('attendance').select('module_id, alumno_id, present').in('alumno_id', alumnoIds),
      ]);

      // El "total" de clases por curso = módulos con end_date pasada.
      // (La app solo inserta registros de attendance cuando alguien marca presente,
      // así que NO podemos derivar "total" del propio attendance — siempre daría 100%.)
      const nowMs = Date.now();
      const moduleCourseMap = new Map<string, string>();
      const pastModulesByCourse = new Map<string, number>();
      (modules ?? []).forEach((m: any) => {
        moduleCourseMap.set(m.id, m.course_id);
        if (!m.end_date) return;
        const endMs = new Date(m.end_date).getTime();
        if (Number.isNaN(endMs) || endMs > nowMs) return;
        pastModulesByCourse.set(m.course_id, (pastModulesByCourse.get(m.course_id) ?? 0) + 1);
      });

      // Asistencias presentes por (alumno_id, curso_id)
      const presentByKey = new Map<string, number>();
      (attendance ?? []).forEach((a: any) => {
        if (!a.present) return;
        const cId = moduleCourseMap.get(a.module_id);
        if (!cId) return;
        const k = `${a.alumno_id}|${cId}`;
        presentByKey.set(k, (presentByKey.get(k) ?? 0) + 1);
      });

      const out: RiskRow[] = enRiesgo.map((e: any) => {
        const k = `${e.alumno_id}|${e.course_id}`;
        const asistidas = presentByKey.get(k) ?? 0;
        const total     = pastModulesByCourse.get(e.course_id) ?? 0;
        const s = { asistidas, total };
        const porc = s.total > 0 ? Math.round((s.asistidas / s.total) * 100) : 0;
        const avg = typeof e.final_average === 'string' ? parseFloat(e.final_average) : e.final_average;
        return {
          alumnoId:        e.alumno_id,
          fullName:        e.alumno?.full_name || 'Sin nombre',
          email:           e.alumno?.email || '',
          cursoId:         e.course_id,
          cursoName:       e.curso?.name || 'Sin curso',
          promedio:        avg,
          clasesAsistidas: s.asistidas,
          clasesTotales:   s.total,
          porcAsistencia:  porc,
        };
      });

      out.sort((a, b) => a.promedio - b.promedio);
      setRows(out);
    } catch (err) {
      console.error('[AlumnosEnRiesgoPage] error:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    if (!term) return rows;
    return rows.filter(r =>
      r.fullName.toLowerCase().includes(term)
      || r.email.toLowerCase().includes(term)
      || r.cursoName.toLowerCase().includes(term)
    );
  }, [rows, search]);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <PageHeader
        eyebrow="Análisis"
        eyebrowIcon={AlertTriangle}
        title="Alumnos en riesgo"
        description="Alumnos con promedio menor a 4.0 — incluye su asistencia para evaluar el caso."
        actions={
          <button onClick={load} disabled={loading} className="btn-glass" title="Recargar">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Recargar</span>
          </button>
        }
      />

      <section className="glass-surface rounded-2xl p-4 border border-white/[0.06]">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="search"
            placeholder="Buscar por alumno o curso…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-cubus-deep border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-cubus-lime/40"
          />
        </div>
      </section>

      <section className="glass-surface rounded-2xl border border-white/[0.06] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-white/60">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-400" />
            <p className="text-sm font-medium text-emerald-300 mb-1">¡Sin alumnos en riesgo!</p>
            <p className="text-xs text-white/40">Todos están sobre el 4.0 de promedio o aún no tienen notas suficientes.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 text-[11px] uppercase tracking-wider text-white/40">
                  <th className="text-left py-3 px-4">Alumno</th>
                  <th className="text-left py-3 px-4">Curso</th>
                  <th className="text-center py-3 px-4">Promedio</th>
                  <th className="text-center py-3 px-4">Clases asistidas</th>
                  <th className="text-center py-3 px-4">Clases faltadas</th>
                  <th className="text-center py-3 px-4">% Asistencia</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, idx) => {
                  const faltadas = Math.max(0, r.clasesTotales - r.clasesAsistidas);
                  const asistColor =
                    r.clasesTotales === 0 ? 'text-white/40'
                    : r.porcAsistencia >= 80 ? 'text-cubus-lime'
                    : r.porcAsistencia >= 70 ? 'text-amber-400'
                    : 'text-rose-400';
                  return (
                    <tr key={`${r.alumnoId}-${r.cursoId}-${idx}`} className="border-b border-white/[0.06] hover:bg-white/[0.03] transition-colors text-sm">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-300 text-xs font-bold">
                            {r.fullName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="text-white font-medium">{r.fullName}</div>
                            <div className="text-[11px] text-white/40">{r.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-white/80">{r.cursoName}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="text-rose-400 font-bold text-base">{r.promedio.toFixed(1)}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className="inline-flex items-center gap-1 text-cubus-lime/90 font-medium">
                          <CheckCircle2 size={14} />{r.clasesAsistidas}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`inline-flex items-center gap-1 font-medium ${faltadas > 0 ? 'text-rose-400' : 'text-white/40'}`}>
                          <XCircle size={14} />{faltadas}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {r.clasesTotales === 0 ? (
                          <span className="text-white/30 text-xs" title="Aún no hay módulos con fecha pasada en este curso">
                            sin clases dictadas aún
                          </span>
                        ) : (
                          <div className="flex flex-col items-center gap-1">
                            <span className={`font-bold ${asistColor}`}>{r.porcAsistencia}%</span>
                            <div className="w-full max-w-[80px] h-1 bg-white/10 rounded-full overflow-hidden">
                              <div
                                className={`h-full rounded-full ${
                                  r.porcAsistencia >= 80 ? 'bg-cubus-lime'
                                  : r.porcAsistencia >= 70 ? 'bg-amber-400'
                                  : 'bg-rose-400'
                                }`}
                                style={{ width: `${r.porcAsistencia}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
